import { Col, Form, InputNumber, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../app/hooks';
import { setModal } from '../../../../common/slices/commonSlice';
import { ISetState } from '../../../../common/types/commonTypes';
import {
  DateInput,
  FormButton,
  FormInputItem,
  TextAreaInput,
} from '../../../../components/common/FormItem/FormItems';
import SelectPaymentMethod from '../../../../components/common/paymentMethod/SelectPaymentMethod';
import { IaccountsByMethod } from '../../../Accounts/AccountsTypes/AccountsTypes';
import {
  useLazyGetAllAccountsByTypeQuery,
  useLazyGetSingleAccountQuery,
} from '../../../Accounts/Api/Endpoints/accountsEndpoints';
import { useGetAuthorityQuery } from '../../Api/Endpoints/AuthorityEndpoints';
import { useLazyGetSingleLoanQuery } from '../../Api/Endpoints/LoanEndpoints';
import {
  useEditReceiveMutation,
  useLazyGetReceivedAuthorityQuery,
} from '../../Api/Endpoints/ReceiveEndpoints';
import {
  payment_type,
  ReceivedEditFormData,
  ViewLoanReceiveDataType,
} from '../../LoanTypes/LoanTypes';
import { useGetAccountByTypeQuery } from '../../../vendor/api/endpoints/vendorPaymentEndpoints';
import AccountLastBalanceInput from '../../../vendor/components/AccountLastBalanceInput';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../auth/states/userSlice';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';

interface IProps {
  info: ViewLoanReceiveDataType;
  setNull: ISetState<ViewLoanReceiveDataType | null>;
}

const EditReceiveModal = ({ info, setNull }: IProps) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [paymentType, setPaymentType] = useState<string>();
  const [accountID, setAccountID] = useState<number>();
  const [authority_id, set_Authority_id] = useState<number>();
  const [loan_id, set_Loan_id] = useState<number>();
  const [due, setDue] = useState<number>();
  const user = useSelector(selectUser);
  const [payMethodCatId, setPayMethodCatId] = useState<number | undefined>();
  const [paymentMethod, setPaymentMethod] = useState(0);
  const [account, setAccount] = useState<string>();
  const [balance, setBalance] = useState<number>();

  const { data: accounts, refetch: refetchAccount } = useGetAccountByTypeQuery(
    Number(payMethodCatId)
  );

  const isChanged = useRef(false);
  const [fetchSingleLoan, { data: loanDetails }] = useLazyGetSingleLoanQuery();
  useEffect(() => {
    form.setFieldsValue({
      authority_id: info.authority_id,
      loan_id: info.loan_id,
      account_id: info.account_id,
      received_date: dayjs(info.received_date),
      withdraw_date:
        info.received_withdraw_date && dayjs(info.received_withdraw_date),
      payment_type: info.payment_type,
      type_id: info.type_id,
      receivable_amount:
        loanDetails?.data && loanDetails?.data[0].loan_receivable_amount,
      due_amount: loanDetails?.data && loanDetails?.data[0].loan_due_amount,
      amount: info.received_amount,
      cheque_no: info.received_cheque_no,
      bank_name: info.received_bank_name,
      payment_note: info.received_note,
    });

    if (info.type_id) {
      setPaymentMethod(info.type_id);
    }

    setPayMethodCatId(
      info.payment_type ? Number(info.payment_type) : undefined
    );

    setAccount(info.account_id?.toString());

    set_Authority_id(info.authority_id);
    setPaymentType(info.payment_type);
    setAccountID(info.account_id);
    set_Loan_id(info.loan_id);
  }, [info]);

  const { data: authority } = useGetAuthorityQuery();

  const [fetchLoansbyID, { data: loansByID }] =
    useLazyGetReceivedAuthorityQuery();
  useEffect(() => {
    if (authority_id) {
      fetchLoansbyID(Number(authority_id));
    }
  }, [authority_id]);

  const [fetchAccountsByPayment, { data: accountsByPayment }] =
    useLazyGetAllAccountsByTypeQuery();
  useEffect(() => {
    if (paymentMethod) {
      fetchAccountsByPayment(Number(paymentMethod));
    }
    if (isChanged.current) {
      form.setFieldsValue({ account_id: undefined });
      setBalance(undefined);

      return;
    }
  }, [paymentType, paymentMethod]);

  const [fetchBalance, { data: accountBalance }] =
    useLazyGetSingleAccountQuery();
  useEffect(() => {
    if (account) {
      fetchBalance(Number(account));
    }
  }, [account, info]);

  useEffect(() => {
    if (loan_id) {
      fetchSingleLoan(loan_id);
    }
  }, [loan_id]);

  useEffect(() => {
    setBalance(accountBalance?.data && accountBalance?.data.amount);
  }, [accountBalance]);

  useEffect(() => {
    if (loanDetails?.data) {
      form.setFieldValue(
        'receivable_amount',
        loanDetails?.data && loanDetails?.data[0].loan_receivable_amount
      );
      form.setFieldValue(
        'due_amount',
        loanDetails?.data && loanDetails?.data[0].loan_due_amount
      );
      setDue(Number(loanDetails?.data[0].loan_due_amount));
    }
  }, [loanDetails, paymentType]);

  const [editReceive, { isError, isLoading }] = useEditReceiveMutation();

  const onFinish = async (values: ReceivedEditFormData) => {
    const body: ReceivedEditFormData = {
      ...values,
      received_id: info.received_id,
      received_date: dayjs(values.received_date).format('YYYY-MM-DD'),
      withdraw_date: dayjs(values.withdraw_date).format('YYYY-MM-DD'),
      created_by: user?.user_id as number,
    };
    form.resetFields();
    form.setFieldsValue({ account_id: info.account_id });

    await editReceive(body);
    dispatch(setModal(false));
    setNull(null);
  };
  //loading indicator
  if (isLoading) {
    return loadingIndicator;
  }
  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 24 }}>
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item
            name='authority_id'
            label='Loan Authority'
            rules={[
              { required: true, message: 'Loan Authority is required!!' },
            ]}
          >
            <Select
              allowClear
              placeholder='Select Loan Authority'
              style={{ width: '100%' }}
              onSelect={(e: number) => {
                set_Authority_id(e);
              }}
            >
              {authority?.data?.map((item, index) => {
                return (
                  <Select.Option key={index} value={item.authority_id}>
                    {item.authority_name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item name='loan_id' label='Select Loan'>
            <Select
              allowClear
              placeholder='Select Loan'
              style={{ width: '100%' }}
              onSelect={(e: number) => {
                set_Loan_id(e);
              }}
            >
              {loansByID?.data?.map((item, index) => {
                return (
                  <Select.Option key={index} value={item.loan_id}>
                    {`${item.loan_name}`}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>

        <SelectPaymentMethod
          required
          name='payment_type'
          label='Payment Method '
          onChange={(value: number) => {
            setPayMethodCatId(value);
            setPaymentMethod(value);
            if (!isChanged.current) isChanged.current = true;
          }}
          cheque
          size={12}
        />
        {Number(payMethodCatId) === 4 && (
          <>
            <FormInputItem
              label='Cheque No'
              name='cheque_no'
              required
              size={12}
            />

            <DateInput
              name='withdraw_date'
              label='Withdraw Date'
              required
              size={12}
            />

            <FormInputItem
              name={'bank_name'}
              label='Bank Name'
              required
              onChange={(value) => setAccount(String(value))}
              size={12}
            />
          </>
        )}
        {Number(payMethodCatId) !== 4 && (
          <>
            <AccountLastBalanceInput
              accountsLastBalance={accounts ? accounts.data : []}
              name='account_id'
              label='Account:'
              // disabled={paymentMethod || reason === 'EDIT' ? false : true}
              onSelect={(value: number) => setAccount(String(value))}
              required
              size={12}
            />
          </>
        )}
        {Number(payMethodCatId) === 3 && (
          <>
            <FormInputItem
              name={'charge_amount'}
              label='Transaction Charge :'
              size={12}
            />

            <FormInputItem
              label='Receipt/Trans No:'
              name='trans_no'
              size={12}
            />
          </>
        )}

        {Number(payMethodCatId) !== 4 && (
          <Col span={6} xs={24} sm={24} md={24} lg={12}>
            <Form.Item label='Available Balance' required>
              <InputNumber style={{ width: '100%' }} disabled value={balance} />
            </Form.Item>
          </Col>
        )}

        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item label='Receivable Amount' name='receivable_amount'>
            <InputNumber style={{ width: '100%' }} disabled />
          </Form.Item>
        </Col>
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item label='Due Amount' name='due_amount'>
            <InputNumber style={{ width: '100%' }} disabled />
          </Form.Item>
        </Col>
        <Col span={6} xs={24} sm={24} md={24} lg={12}>
          <Form.Item
            label='Receive Amount'
            name='amount'
            rules={[
              { required: true, message: 'Payment Amount is required!' },
              {
                validator: (_, value) => {
                  if (value && due) {
                    if (value > due) {
                      return Promise.reject('Amount cannot be more than due');
                    }
                    if (balance && value > balance) {
                      return Promise.reject('Insufficient Balance');
                    } else {
                      return Promise.resolve();
                    }
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <DateInput label='Date' name='received_date' size={12} />
        <TextAreaInput label='Note' name='received_note' size={24} />
      </Row>
      <FormButton label='Edit Payment' loading={isLoading} />
    </Form>
  );
};

export default EditReceiveModal;
